import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLenderContacts } from "@/components/LenderOutreach";
import { api, errMsg } from "@/lib/api";

const field = "bg-slate-950/60 border-slate-700";
const EMPTY = { name: "", title: "", bank: "", email: "", phone: "", address: "" };
const FIELDS = [["name", "Name", true], ["title", "Title"], ["bank", "Bank or lender"], ["email", "Email"], ["phone", "Phone"]];

const ContactDialog = ({ edit, setEdit, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...edit, email: edit.email || null };
    try { await (edit.id ? api.put(`/lender-contacts/${edit.id}`, body) : api.post("/lender-contacts", body)); toast.success("Contact saved"); setEdit(null); onSaved(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg" data-testid="lender-contact-dialog">
        <DialogHeader><DialogTitle className="font-display">{edit?.id ? "Edit loan officer" : "Add loan officer"}</DialogTitle></DialogHeader>
        {edit && <form onSubmit={save} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">{FIELDS.map(([k, l, req]) => (
            <div key={k} className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">{l}</Label><Input required={req} type={k === "email" ? "email" : "text"} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className={field} data-testid={`lender-contact-${k}`} /></div>
          ))}</div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Mailing address</Label><Textarea rows={2} value={edit.address || ""} onChange={(e) => setEdit({ ...edit, address: e.target.value })} className={field} data-testid="lender-contact-address" /></div>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400" data-testid="lender-contact-save">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save contact</Button>
        </form>}
      </DialogContent>
    </Dialog>
  );
};

export default function LenderContacts() {
  const [list, load, loading] = useLenderContacts();
  const [edit, setEdit] = useState(null);
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} from lender contacts?`)) return;
    try { await api.delete(`/lender-contacts/${c.id}`); toast.success("Contact removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 fade-up" data-testid="lender-contacts-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Lender contacts</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">Loan officers</h1>
          <p className="text-slate-400 max-w-2xl">Pick these when addressing a cover letter or sending a study. Referral partners appear automatically and are edited on the <Link to="/partners" className="text-emerald-400 underline underline-offset-4">Partners</Link> page.</p>
        </div>
        <Button onClick={() => setEdit({ ...EMPTY })} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400" data-testid="lender-contact-add"><Plus className="h-4 w-4 mr-1" />Add loan officer</Button>
      </div>
      <div className="rounded-xl border border-slate-800 divide-y divide-slate-800" data-testid="lender-contacts-list">
        {loading && <p className="p-6 text-sm text-slate-500 flex items-center gap-2" data-testid="lender-contacts-loading"><Loader2 className="h-4 w-4 animate-spin" />Loading contacts</p>}
        {!loading && !list.length && <p className="p-6 text-sm text-slate-500">No loan officers yet.</p>}
        {list.map((c) => (
          <div key={`${c.source}:${c.id}`} className="p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`lender-contact-row-${c.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-sm">{c.name}<span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">{c.source === "partner" ? "Referral partner" : "Contact"}</span></p>
              <p className="text-xs text-slate-400 break-all">{[c.title, c.bank, c.email, c.phone].filter(Boolean).join(" · ")}</p>
            </div>
            {c.source === "contact" && <div className="flex gap-1">
              <Button size="icon" variant="ghost" title="Edit" onClick={() => setEdit({ ...EMPTY, ...c, email: c.email || "" })} data-testid={`lender-contact-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" title="Remove" onClick={() => remove(c)} className="text-rose-300" data-testid={`lender-contact-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>}
          </div>
        ))}
      </div>
      <ContactDialog edit={edit} setEdit={setEdit} onSaved={load} />
    </div>
  );
}
